import { DatePipe, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { GrooveSurfaceDirective } from '@groove/angular';
import { CATEGORY_LABELS, LEVEL_LABELS } from '../../../blog/blog-categories';
import {
  blogPostBreadcrumbItems,
  buildBlogPostSchemas,
} from '../../../blog/blog-jsonld';
import { BLOG_POSTS } from '../../../blog/blog-posts-token';
import { DisclosureGroupComponent } from '../../../content/disclosure/disclosure-group.component';
import { DisclosureComponent } from '../../../content/disclosure/disclosure.component';
import { PageCtaComponent } from '../../../content/page-cta/page-cta.component';
import { YoutubeSectionComponent } from '../../../content/youtube-section/youtube-section.component';
import { BRAND_SUFFIX, PageTitleService } from '../../../page-title';
import { RouteUiDataService } from '../../../route-ui-data';
import { SEOService } from '../../../seo.service';

const MORE_IN_CATEGORY_LIMIT = 6;

@Component({
  selector: 'app-blog-post',
  imports: [
    DatePipe,
    NgOptimizedImage,
    RouterLink,
    GrooveSurfaceDirective,
    YoutubeSectionComponent,
    DisclosureGroupComponent,
    DisclosureComponent,
    PageCtaComponent,
  ],
  templateUrl: './blog-post.component.html',
  styleUrl: './blog-post.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Markdown body is rendered via [innerHTML], so dynamic <a>/<p>/<h2>/<img>
  // elements don't carry the component-scoped _ngcontent attribute. Using None
  // lets the .blog-post-body descendant styles reach them.
  encapsulation: ViewEncapsulation.None,
})
export class BlogPostComponent implements OnDestroy {
  private sanitizer = inject(DomSanitizer);
  private pageTitle = inject(PageTitleService);
  private seo = inject(SEOService);
  private routeUiData = inject(RouteUiDataService);
  private allPosts = inject(BLOG_POSTS);

  readonly slug = input.required<string>();

  readonly post = computed(() =>
    this.allPosts.find((p) => p.slug === this.slug()),
  );

  readonly bodyHtml = computed<SafeHtml>(() =>
    this.sanitizer.bypassSecurityTrustHtml(this.post()?.htmlBody ?? ''),
  );

  readonly categoryLabel = computed(() => {
    const cat = this.post()?.category;
    return cat ? (CATEGORY_LABELS[cat] ?? cat) : '';
  });

  readonly levelLabel = computed(() => {
    const level = this.post()?.level;
    return level ? (LEVEL_LABELS[level] ?? level) : '';
  });

  readonly youtubeResource = computed<`video:${string}` | null>(() => {
    const id = this.post()?.youtubeId;
    return id ? `video:${id}` : null;
  });

  // Per-video hero placeholder rendition from docs/youtube-thumbnails.json
  // (via build-blog.mts) — YouTube doesn't generate every rendition for every
  // video. Fallbacks match the one rendition every video has (hqdefault.jpg).
  readonly thumbQuality = computed(() => this.post()?.thumbQuality ?? 'high');
  readonly thumbWebp = computed(() => this.post()?.thumbWebp ?? false);

  /** front-door rail: every other front-door post, newest first. */
  readonly siblingFrontDoorPosts = computed(() => {
    const current = this.post();
    if (current?.type !== 'front-door') return [];
    return this.allPosts.filter(
      (p) => p.type === 'front-door' && p.slug !== current.slug,
    );
  });

  /** library rail: same category, newest first, max 6. */
  readonly moreInCategory = computed(() => {
    const current = this.post();
    if (current?.type !== 'library') return [];
    return this.allPosts
      .filter((p) => p.category === current.category && p.slug !== current.slug)
      .slice(0, MORE_IN_CATEGORY_LIMIT);
  });

  constructor() {
    effect(() => {
      const post = this.post();
      if (!post) {
        this.pageTitle.setPageTitle(`Post not found${BRAND_SUFFIX}`, {
          title: 'Post not found',
        });
        this.seo.clearRouteSchemas();
        this.routeUiData.setBreadcrumbOverride([
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
          { name: 'Post not found' },
        ]);
        return;
      }
      const fullTitle = `${post.title}${BRAND_SUFFIX}`;
      // Deliberately no `lead`: the excerpt serves meta description, JSON-LD,
      // and index cards, but rendering it above an answer-first opening
      // paragraph duplicated the post's own first lines (Jeremy, 2026-08-10).
      this.pageTitle.setPageTitle(fullTitle, {
        title: post.title,
      });
      this.seo.setRouteDescription(post.excerpt);
      this.seo.setRouteSchemas(buildBlogPostSchemas(post));
      this.routeUiData.setBreadcrumbOverride(blogPostBreadcrumbItems(post));
    });
  }

  ngOnDestroy(): void {
    this.seo.clearRouteSchemas();
  }
}
